import type { ReponseErreur } from '@gsm/partage';

const BASE = '/api/v1';

/** Erreur remontée par l'API, porteuse du code métier (doc 03, § 6). */
export class ErreurApi extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly statut: number,
    public readonly details?: { champ?: string; message: string }[],
  ) {
    super(message);
    this.name = 'ErreurApi';
  }
}

async function appeler<T>(chemin: string, options: RequestInit = {}): Promise<T> {
  const multipart = options.body instanceof FormData;
  let reponse: Response;
  try {
    reponse = await fetch(`${BASE}${chemin}`, {
      // Le cookie de session est httpOnly : il ne peut être transmis
      // qu'en incluant explicitement les identifiants de connexion.
      credentials: 'include',
      ...options,
      headers: multipart
        ? (options.headers ?? {})
        : { 'Content-Type': 'application/json', ...options.headers },
    });
  } catch {
    throw new ErreurApi('RESEAU_INDISPONIBLE', 'Le serveur est injoignable.', 0);
  }

  if (reponse.status === 204) return undefined as T;

  const corps: unknown = await reponse.json().catch(() => null);

  if (!reponse.ok) {
    const erreur = (corps ?? {}) as Partial<ReponseErreur>;
    // Un corps illisible ne vient pas de l'API — elle répond toujours en JSON
    // normalisé. C'est un intermédiaire qui a répondu à sa place : proxy de
    // développement quand l'API est éteinte, ou passerelle en production. Le
    // dire, plutôt que de rendre un « une erreur est survenue » qui envoie
    // chercher le défaut dans le métier.
    throw new ErreurApi(
      erreur.code ?? 'ERREUR_INTERNE',
      erreur.message ??
        `Réponse illisible du serveur (HTTP ${reponse.status}). L’API est-elle démarrée ?`,
      reponse.status,
      erreur.details,
    );
  }

  return corps as T;
}

export const api = {
  get: <T>(chemin: string) => appeler<T>(chemin, { method: 'GET' }),
  post: <T>(chemin: string, corps?: unknown) =>
    appeler<T>(chemin, {
      method: 'POST',
      body: corps === undefined ? undefined : JSON.stringify(corps),
    }),
  patch: <T>(chemin: string, corps: unknown) =>
    appeler<T>(chemin, { method: 'PATCH', body: JSON.stringify(corps) }),
  /** Remplacement intégral d'une ressource — à ne pas confondre avec `patch`. */
  put: <T>(chemin: string, corps: unknown) =>
    appeler<T>(chemin, { method: 'PUT', body: JSON.stringify(corps) }),
  /**
   * Dépôt de fichier. Le `Content-Type` est délibérément omis : le navigateur
   * doit le poser lui-même pour y inclure la frontière multipart.
   */
  deposer: <T>(chemin: string, formulaire: FormData) =>
    appeler<T>(chemin, { method: 'POST', body: formulaire, headers: {} }),
};

export interface Profil {
  id: string;
  login: string;
  nom: string;
  prenom: string;
  email: string;
  roles: string[];
  libellesRoles: string[];
  doitChangerMotDePasse: boolean;
}
